<template>
    <div class="content">
        <base-header class="pb-6">
            <div class="row align-items-center py-4">
                <div class="col-lg-6 col-7">
                    <h6 class="h2 text-white d-inline-block mb-0">
                        {{ $t("app_catalog.wms_product_title") }}
                    </h6>
                    <nav aria-label="breadcrumb" class="d-none d-md-inline-block ml-md-4">
                        <route-bread-crumb></route-bread-crumb>
                    </nav>
                </div>
                <div class="col-lg-6 col-5 text-right">
                    <base-button size="md" type="success" :loading="syncing" @click="doSync" class="mr-2">
                        <i class="fa fa-refresh"></i> {{ $t("app_catalog.wms_product_sync_btn") }}
                    </base-button>
                    <base-button size="md" type="primary" @click="openImportDialog">
                        <i class="fa fa-download"></i> {{ $t("app_catalog.wms_product_import_btn") }}
                    </base-button>
                </div>
            </div>
        </base-header>

        <div class="container-fluid mt--6">
            <card>
                <div slot="header" class="d-flex justify-content-between align-items-center">
                    <h4 class="mb-0">{{ $t("app_catalog.wms_product_title") }}</h4>
                    <base-button size="sm" type="info" :loading="loadingList" @click="loadList">
                        <i class="fa fa-refresh"></i> {{ $t("app_catalog.wms_product_refresh") }}
                    </base-button>
                </div>

                <el-table
                    v-loading="loadingList"
                    :data="list"
                    size="small"
                    style="width: 100%;"
                >
                    <el-table-column :label="$t('app_catalog.col_image')" width="80px" align="center">
                        <template slot-scope="scope">
                            <img
                                v-if="scope.row.image"
                                :src="listImg(scope.row.image)"
                                style="width: 40px; height: 40px; object-fit: cover;"
                                class="rounded"
                                @error="$event.target.style.display = 'none'"
                            />
                        </template>
                    </el-table-column>
                    <el-table-column :label="$t('app_catalog.col_name_th')" prop="name" min-width="180px">
                    </el-table-column>
                    <el-table-column :label="$t('app_catalog.wms_product_sku')" prop="man_pn" min-width="120px">
                    </el-table-column>
                    <el-table-column :label="$t('app_catalog.wms_product_stock')" min-width="160px">
                        <template slot-scope="scope">
                            <div class="d-flex align-items-center" style="gap: 8px;">
                                <el-input-number
                                    v-model="scope.row._editStock"
                                    :min="0"
                                    :precision="0"
                                    :controls="false"
                                    size="small"
                                    style="width: 90px;"
                                ></el-input-number>
                                <base-button
                                    v-if="scope.row._editStock !== scope.row._origAvailable"
                                    size="sm"
                                    type="primary"
                                    :loading="scope.row._savingStock"
                                    @click="saveStock(scope.row)"
                                >
                                    {{ $t("save") }}
                                </base-button>
                            </div>
                        </template>
                    </el-table-column>
                </el-table>
            </card>
        </div>

        <!--
            Import dialog: a PAGE of the Backoffice wms_products list, searchable.
            It loads page 1 on @open (owner 2026-10-03: an empty modal reads as
            "there is nothing to import"), and the search box narrows the same
            list rather than being the only way to populate it.
        -->
        <el-dialog
            :title="$t('app_catalog.wms_product_import_title')"
            :visible.sync="importDialogVisible"
            width="1100px"
            custom-class="wms-import-dialog"
            append-to-body
            @open="onImportDialogOpen"
        >
            <div class="mb-3 d-flex" style="gap: 8px;">
                <el-input
                    v-model="importQuery"
                    :placeholder="$t('app_catalog.wms_product_search_placeholder')"
                    size="small"
                    style="max-width: 320px;"
                    clearable
                    @keyup.enter.native="searchWms"
                    @clear="searchWms"
                ></el-input>
                <base-button size="sm" type="info" :loading="importSearchLoading" @click="searchWms">
                    <i class="fa fa-search"></i> {{ $t("app_catalog.wms_product_search_btn") }}
                </base-button>
            </div>

            <el-table
                v-loading="importSearchLoading"
                :data="importResults"
                size="small"
                max-height="420"
                style="width: 100%;"
                @selection-change="onImportSelectionChange"
            >
                <el-table-column
                    type="selection"
                    width="50px"
                    :selectable="rowSelectable"
                ></el-table-column>
                <el-table-column :label="$t('app_catalog.col_image')" width="70px" align="center">
                    <template slot-scope="scope">
                        <img
                            v-if="scope.row.image"
                            :src="wmsImg(scope.row.image)"
                            style="width: 48px; height: 48px; object-fit: cover;"
                            class="rounded"
                            @error="$event.target.style.display = 'none'"
                        />
                    </template>
                </el-table-column>
                <el-table-column :label="$t('app_catalog.col_name_th')" min-width="260px">
                    <template slot-scope="scope">
                        <span>{{ scope.row.name }}</span>
                        <el-tag
                            v-if="!rowSelectable(scope.row)"
                            size="mini"
                            type="info"
                            class="ml-2"
                            data-test="wms-imported-badge"
                        >
                            {{ $t("app_catalog.wms_product_imported") }}
                        </el-tag>
                    </template>
                </el-table-column>
                <el-table-column :label="$t('app_catalog.wms_product_sku')" prop="man_pn" min-width="140px"></el-table-column>
                <el-table-column
                    :label="$t('app_catalog.wms_product_price')"
                    min-width="110px"
                    align="right"
                >
                    <template slot-scope="scope">
                        {{ formatPrice(scope.row.price) }}
                    </template>
                </el-table-column>
            </el-table>

            <div class="mt-3 d-flex justify-content-between align-items-center flex-wrap" style="gap: 8px;">
                <small class="text-muted" data-test="wms-import-total">
                    {{ $t("app_catalog.wms_product_total", { count: importTotal }) }}
                </small>
                <el-pagination
                    background
                    layout="prev, next, jumper"
                    :total="importTotal"
                    :page-size="importPageSize"
                    :current-page="importPage"
                    @current-change="onImportPageChange"
                ></el-pagination>
            </div>

            <span slot="footer" class="dialog-footer">
                <base-button type="secondary" @click="importDialogVisible = false">
                    {{ $t("cancel") }}
                </base-button>
                <base-button
                    type="primary"
                    :loading="importing"
                    :disabled="importSelected.length === 0"
                    @click="doImport"
                >
                    {{ $t("app_catalog.wms_product_import_confirm") }} ({{ importSelected.length }})
                </base-button>
            </span>
        </el-dialog>
    </div>
</template>

<script>
import { apiErrorMessage } from "@/util/api-error";
import { mapGetters, mapActions } from "vuex";
import {
    Table,
    TableColumn,
    InputNumber,
    Input,
    Dialog,
    Tag,
    Pagination
} from "element-ui";
// The picker's rules (params, price text, which rows may be ticked, the total)
// live in a Vue-free module because this repo's jest cannot mount a .vue file
// (reference-bo-fn-jest-infra). See tests/unit/wmsImportModal.spec.js.
import {
    WMS_PICKER_PAGE_SIZE,
    formatWmsPrice,
    wmsRowSelectable
} from "@/utils/wmsImportModal";

export default {
    name: "AppCatalogWmsProductList",
    components: {
        [Table.name]: Table,
        [TableColumn.name]: TableColumn,
        [InputNumber.name]: InputNumber,
        [Input.name]: Input,
        [Dialog.name]: Dialog,
        [Tag.name]: Tag,
        [Pagination.name]: Pagination
    },
    data() {
        return {
            loadingList: false,
            syncing: false,
            importDialogVisible: false,
            importQuery: "",
            importSearchLoading: false,
            importResults: [],
            importSelected: [],
            importing: false,
            importPage: 1,
            importPageSize: WMS_PICKER_PAGE_SIZE,
            importTotal: 0
        };
    },
    computed: {
        ...mapGetters({
            list: "AppCatalogWmsProduct/list"
        })
    },
    mounted() {
        this.loadList();
    },
    methods: {
        ...mapActions({
            fetchPgList: "AppCatalogWmsProduct/fetchPgList",
            listBoProductsAction: "AppCatalogWmsProduct/listBoProducts",
            importProductsAction: "AppCatalogWmsProduct/importProducts",
            setStockAction: "AppCatalogWmsProduct/setStock",
            syncFromBackoffice: "AppCatalogWmsProduct/syncFromBackoffice"
        }),
        wmsImg(filename) {
            return filename ? (process.env.VUE_APP_API_BASE_URL + "/files/wms_products/" + filename) : "";
        },
        listImg(v) {
            if (!v) return "";
            return /^https?:\/\//i.test(v) ? v : this.wmsImg(v);
        },
        async loadList() {
            this.loadingList = true;
            try {
                await this.fetchPgList({});
                // Attach editable stock fields to each row reactively.
                this.list.forEach(row => {
                    if (row._editStock === undefined) {
                        this.$set(row, "_editStock", row.available || 0);
                        this.$set(row, "_origAvailable", row.available || 0);
                        this.$set(row, "_savingStock", false);
                    }
                });
            } catch (err) {
                const msg = apiErrorMessage(err, this.$t);
                this.$notify({ type: "danger", message: msg });
            } finally {
                this.loadingList = false;
            }
        },
        async saveStock(row) {
            this.$set(row, "_savingStock", true);
            try {
                await this.setStockAction({
                    wmsId: row.wms_product_id,
                    payload: { available: row._editStock || 0 }
                });
                this.$set(row, "available", row._editStock);
                this.$set(row, "_origAvailable", row._editStock);
                this.$notify({ type: "success", message: this.$t("app_catalog.wms_product_stock_saved") });
            } catch (err) {
                const msg = apiErrorMessage(err, this.$t);
                this.$notify({ type: "danger", message: msg });
            } finally {
                this.$set(row, "_savingStock", false);
            }
        },
        async doSync() {
            this.syncing = true;
            try {
                const res = await this.syncFromBackoffice();
                const count = (res && res.data && res.data.synced !== undefined) ? res.data.synced : null;
                const msg = count !== null
                    ? this.$t("app_catalog.wms_product_sync_success") + " (" + count + ")"
                    : this.$t("app_catalog.wms_product_sync_success");
                this.$notify({ type: "success", message: msg });
                await this.loadList();
            } catch (err) {
                const msg = apiErrorMessage(err, this.$t);
                this.$notify({ type: "danger", message: msg });
            } finally {
                this.syncing = false;
            }
        },
        // Price text and "may this row be ticked" come from the shared module so
        // the template cannot drift from what the specs pin down.
        formatPrice(v) {
            return formatWmsPrice(v);
        },
        rowSelectable(row) {
            return wmsRowSelectable(row);
        },
        openImportDialog() {
            this.importQuery = "";
            this.importResults = [];
            this.importSelected = [];
            this.importPage = 1;
            this.importTotal = 0;
            // The first page is fetched by @open (onImportDialogOpen) so the list
            // is also there when the dialog is reopened via :visible.sync.
            this.importDialogVisible = true;
        },
        onImportDialogOpen() {
            this.loadImportPage(1);
        },
        onImportPageChange(page) {
            this.loadImportPage(page);
        },
        // Search is "page 1 of the filtered list" — same request, same renderer.
        // Enter, the ค้นหา button and clearing the box all land here.
        searchWms() {
            return this.loadImportPage(1);
        },
        async loadImportPage(page) {
            this.importSearchLoading = true;
            try {
                const res = await this.listBoProductsAction({
                    q: this.importQuery,
                    page: page,
                    pageSize: this.importPageSize
                });
                this.importResults = res.rows;
                this.importTotal = res.total;
                this.importPage = res.page;
            } catch (err) {
                const msg = apiErrorMessage(err, this.$t);
                this.$notify({ type: "danger", message: msg });
            } finally {
                this.importSearchLoading = false;
            }
        },
        onImportSelectionChange(selection) {
            this.importSelected = selection;
        },
        async doImport() {
            if (this.importSelected.length === 0) return;
            this.importing = true;
            try {
                const ids = this.importSelected.map(r => r.id);
                await this.importProductsAction({ ids });
                this.$notify({ type: "success", message: this.$t("app_catalog.wms_product_import_success") });
                this.importDialogVisible = false;
                await this.loadList();
            } catch (err) {
                const msg = apiErrorMessage(err, this.$t);
                this.$notify({ type: "danger", message: msg });
            } finally {
                this.importing = false;
            }
        }
    }
};
</script>

<!--
    NOT scoped: the dialog is append-to-body, so a scoped rule would never reach
    it. `width="1100px"` is an inline style on .el-dialog; max-width beats it, so
    the modal stops growing past the viewport on a laptop/tablet instead of
    hanging off the right edge.
-->
<style>
.wms-import-dialog {
    max-width: 96vw;
}
.wms-import-dialog .el-dialog__body {
    padding-top: 12px;
}
</style>
